import { useEffect, useRef } from "react";
import { useCart } from "@shopify/hydrogen-react";
import { useMapStore } from "@/store/useMapStore";
import { useToast } from "@/hooks/use-toast";

// Keys that should be visible to the customer (no underscore prefix)
const VISIBLE_PROPS = new Set(['Address', 'frame']);

export const useShopifyStore = () => {
  const { toast } = useToast();
  const { activeVariant, getCustomizationData } = useMapStore();
  const { linesAdd, status, error } = useCart();
  const pendingAddRef = useRef(false);

  // Watch cart status to show success/error after linesAdd completes.
  // linesAdd returns void (fire-and-forget); the cart transitions through:
  // - Existing cart: idle → updating → idle (success) or idle + error (failure)
  // - No cart yet: uninitialized → creating → idle (success) or uninitialized + error (failure)
  // - Recovering cart: fetching → idle (then linesAdd triggers updating)
  useEffect(() => {
    if (!pendingAddRef.current) return;
    if (status === 'updating' || status === 'creating' || status === 'fetching') return;

    pendingAddRef.current = false;
    if (error) {
      console.error('[useShopifyStore] Add to cart error:', error);
      toast({
        title: "Error",
        description: "Failed to add to cart. Please try again.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Added to Cart",
        description: "Your custom map has been added to the cart",
      });
    }
  }, [status, error, toast]);

  const addToCart = () => {
    // Allow add-to-cart when idle (existing cart) or uninitialized (no cart yet —
    // linesAdd will trigger cartCreate internally). Block during in-flight operations.
    if (status === 'fetching' || status === 'creating' || status === 'updating') {
      toast({
        title: "Please Wait",
        description: "Cart is loading. Please try again in a moment.",
      });
      return;
    }

    const { selectedOptions, variants } = useMapStore.getState();

    if (!activeVariant) {
      console.error('[useShopifyStore] No active variant selected');
      console.error('[useShopifyStore] Current selectedOptions:', selectedOptions);
      console.error('[useShopifyStore] Available variants:', variants);

      toast({
        title: "Cannot Add to Cart",
        description: "Please select a product size or wait for the page to fully load.",
        variant: "destructive",
      });
      return;
    }

    const customizationData = getCustomizationData();

    // Build attributes array for Storefront API cartLinesAdd.
    // Shopify maps cart line `attributes` to `line_item.properties` in the
    // order webhook. The `_` prefix hides keys from customer-facing surfaces.
    const attributes: { key: string; value: string }[] = [];
    for (const [key, value] of Object.entries(customizationData)) {
      if (value === null || value === undefined) continue;
      const finalValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
      const propKey = VISIBLE_PROPS.has(key) ? key : `_${key}`;
      attributes.push({ key: propKey, value: finalValue });
    }

    pendingAddRef.current = true;
    linesAdd([
      {
        merchandiseId: activeVariant.id,
        quantity: 1,
        attributes,
      },
    ]);
  };

  return { addToCart };
};
